import type { FeatureLike } from 'ol/Feature';
import { Circle, LineString, Point } from 'ol/geom';
import {
  Fill,
  Stroke,
  Style,
  Text,
} from 'ol/style';
import CircleStyle from 'ol/style/Circle';

import { isNonZero } from '@nora-monorepo/shared';

import { formatLength } from '#nora-ui/map/drawtools/styles/ruler-style';
import type { FeatureStyleProperties } from '#nora-ui/map/feature-properties';
import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import { defaultFillColor, defaultLineColor, defaultLineWidth } from '#nora-ui/map/map/DrawToolUIManager';

interface HandleProperties {
  type?: string;
}

function getCoordinatePair(coordinates: number[]): [number, number] | null {
  const [x, y] = coordinates;

  if (x === undefined || y === undefined) {
    return null;
  }

  return [x, y];
}

function createRadiusLineStyle(
  center: [number, number],
  radius: number,
  lineColor: string,
  lineWidth: number,
): Style {
  const [centerX, centerY] = center;

  return new Style({
    geometry: new LineString([
      center,
      [centerX + radius, centerY],
    ]),
    stroke: new Stroke({
      color: lineColor,
      lineDash: [4, 8],
      width: Math.max(1, lineWidth * 0.4),
    }),
  });
}

function createRadiusLabelStyle(
  center: [number, number],
  radius: number,
): Style {
  const [centerX, centerY] = center;
  const radiusLine = new LineString([
    center,
    [centerX + radius, centerY],
  ]);

  return new Style({
    geometry: new Point(radiusLine.getCoordinateAt(0.5)),
    text: new Text({
      text: formatLength(radiusLine),
      font: '12px Calibri,sans-serif',
      fill: new Fill({
        color: 'rgba(255, 255, 255, 1)',
      }),
      backgroundFill: new Fill({
        color: 'rgba(0, 0, 0, 0.7)',
      }),
      padding: [2, 2, 2, 2],
      textBaseline: 'bottom',
      offsetY: -8,
    }),
  });
}

function appendRadiusStyles(
  styles: Style[],
  center: [number, number],
  radius: number,
  lineColor: string,
  lineWidth: number,
  radiusLineVisible?: boolean,
): Style[] {
  const isRadiusLineVisible = radiusLineVisible ?? true;

  if (!isRadiusLineVisible || radius <= 0) {
    return styles;
  }

  styles.push(createRadiusLineStyle(center, radius, lineColor, lineWidth));
  styles.push(createRadiusLabelStyle(center, radius));

  return styles;
}

export function pointWithRadiusStyle(feature: FeatureLike): Style[] {
  const {
    radius,
    fillColor,
    lineColor,
    lineWidth,
    radiusLineVisible,
  } = getFeatureProperties<FeatureStyleProperties>(feature);
  const geometry = feature.getGeometry();

  if (!(geometry instanceof Point)) {
    return [new Style()];
  }

  const center = getCoordinatePair(geometry.getCoordinates());
  if (!center) {
    return [new Style()];
  }

  const circleRadius = radius ?? 0;
  const resolvedLineColor = lineColor ?? defaultLineColor;
  const resolvedLineWidth = lineWidth ?? defaultLineWidth;
  const styles = [new Style({
    fill: new Fill({
      color: fillColor ?? defaultFillColor,
    }),
    stroke: new Stroke({
      color: resolvedLineColor,
      width: resolvedLineWidth,
    }),
    geometry: new Circle(center, circleRadius),
  })];

  return appendRadiusStyles(
    styles,
    center,
    circleRadius,
    resolvedLineColor,
    resolvedLineWidth,
    radiusLineVisible,
  );
}

export function circleStyleFunction(feature: FeatureLike): Style[] {
  const {
    fillColor,
    lineColor,
    lineWidth,
    radius,
    radiusLineVisible,
  } = getFeatureProperties<FeatureStyleProperties>(feature);
  const geometry = feature.getGeometry();
  if (geometry instanceof Circle) {
    const center = getCoordinatePair(geometry.getCenter());
    const circleRadius = geometry.getRadius();
    const resolvedLineColor = lineColor ?? defaultLineColor;
    const resolvedLineWidth = lineWidth ?? defaultLineWidth;
    const styles = [new Style({
      geometry,
      fill: new Fill({
        color: fillColor ?? defaultFillColor,
      }),
      stroke: new Stroke({
        color: resolvedLineColor,
        width: resolvedLineWidth,
      }),
    })];

    if (!center) {
      return styles;
    }

    return appendRadiusStyles(
      styles,
      center,
      circleRadius,
      resolvedLineColor,
      resolvedLineWidth,
      radiusLineVisible,
    );
  }

  if (isNonZero(radius)) {
    return pointWithRadiusStyle(feature);
  }

  // Default style if still doesn't match
  return [new Style({
    stroke: new Stroke({
      color: 'black',
      width: 2,
    }),
  })];
}

/**
 * Styles the handles use to move/resize the circle.
 * Probably place this somewhere else for the next tool that uses these handles.
 */
export function getHandleStyle(feature: FeatureLike): Style {
  const fillColor = 'white';
  const { type } = getFeatureProperties<HandleProperties>(feature);

  let icon: string | undefined;
  if (type === 'center-handle') {
    icon = 'move_custom';
  }

  return new Style({
    image: new CircleStyle({
      radius: 8,
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: 'black', width: 2 }),
    }),
    text: new Text({
      font: '12px "grammar-icons"',
      text: icon,
      fill: new Fill({ color: 'black' }),
      offsetY: 0,
    }),
  });
}
